import { useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Gauge,
  Loader2,
  Mail,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type {
  PartnerCampaignOverviewRow,
  PartnerSenderHealthRow,
  PartnerMemberQuotaRow,
} from '@/hooks/useEnterpriseEmailMonitoring';
import {
  useEnterpriseCampaignFailures,
  type FailureRow,
} from '@/hooks/useEnterpriseCampaignFailures';
import type { PartnerEmulationActivityRow } from '@/hooks/useEnterpriseEmulationActivity';

/**
 * Inline detail slide-out for the enterprise monitoring/triage surfaces.
 *
 * Replaces the (unworkable) cross-workspace deep-link "View" action: enterprise
 * admins are NOT members of the partner workspaces they monitor, so
 * `WorkspaceContext.switchWorkspace` early-returns and the jump silently does
 * nothing. Instead, "View"/"Details" now opens THIS right-side `Sheet` and
 * renders the underlying row from data that is already loaded — no workspace
 * switch, no navigation, no extra fetch.
 *
 * The sheet is purely a read surface. Each record type ends with a muted note
 * pointing the admin at the in-workspace remediation (which a workspace MEMBER
 * must perform) — the admin never auto-acts from here.
 */

/** A campaign that an admin opened from the campaign overview / alert queue. */
export interface EnterpriseCampaignDetailRecord {
  type: 'campaign';
  data: PartnerCampaignOverviewRow;
}

/** A sender account opened from the sender-health panel / alert queue. */
export interface EnterpriseSenderDetailRecord {
  type: 'sender';
  data: PartnerSenderHealthRow;
}

/** A member's daily-quota row opened from the alert queue. */
export interface EnterpriseQuotaDetailRecord {
  type: 'quota';
  data: PartnerMemberQuotaRow;
}

/** A security-forced emulation closure opened from the alert queue. */
export interface EnterpriseEmulationDetailRecord {
  type: 'emulation';
  data: PartnerEmulationActivityRow;
}

export type EnterpriseDetailRecord =
  | EnterpriseCampaignDetailRecord
  | EnterpriseSenderDetailRecord
  | EnterpriseQuotaDetailRecord
  | EnterpriseEmulationDetailRecord;

interface EnterpriseRecordDetailSheetProps {
  /** The record to display; `null` keeps the sheet closed. */
  record: EnterpriseDetailRecord | null;
  /** Forwarded to the underlying Sheet — fires with `false` on close. */
  onOpenChange: (open: boolean) => void;
}

/** Render a 0..1 rate as a percentage, or "—" when it isn't a finite number. */
function formatRate(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

/** A muted, optional value — falls back to "—" for empty/nullish input. */
function display(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  return String(value);
}

/** One label/value detail line. */
function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm font-medium break-words">
        {children}
      </span>
    </div>
  );
}

function YesNo({ value }: { value: boolean }): JSX.Element {
  return value ? (
    <Badge variant="destructive" className="gap-1">
      <AlertTriangle className="h-3 w-3" />
      Yes
    </Badge>
  ) : (
    <span className="text-muted-foreground">No</span>
  );
}

/** Render a failure row's relative time, falling back to the raw string. */
function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatDistanceToNow(d, { addSuffix: true });
}

function FailureRowItem({ row }: { row: FailureRow }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const reason = row.reason ?? '—';
  const isLong = reason.length > 120;
  const shown = expanded || !isLong ? reason : `${reason.slice(0, 120)}…`;
  return (
    <div className="py-2">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 break-words text-sm font-medium">
          {row.recipient_email}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {relativeTime(row.last_attempt_at)}
        </span>
      </div>
      <div className="mt-1 flex items-start gap-2">
        {row.response_code != null && (
          <Badge variant="outline" className="shrink-0 font-mono text-[11px]">
            {row.response_code}
          </Badge>
        )}
        <span className="min-w-0 break-words text-sm text-muted-foreground">
          {shown}
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="ml-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

function CampaignFailuresSection({
  campaignId,
  emailsFailed,
}: {
  campaignId: string;
  emailsFailed: number;
}): JSX.Element | null {
  // Hidden entirely (and no query) when there are no failures to triage.
  const enabled = emailsFailed > 0;
  const { rows, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useEnterpriseCampaignFailures(campaignId, { enabled });

  if (!enabled) return null;

  return (
    <div className="mt-6 border-t pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Failures</h3>
        <span className="text-xs text-muted-foreground">
          {emailsFailed} reported
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        View only — contact the workspace admin to resolve.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading failures…
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-destructive">
          Couldn&rsquo;t load failure details. Try reopening this campaign.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">
          Some failure details may have been cleaned up.
        </p>
      ) : (
        <>
          <div className="divide-y">
            {rows.map((row, i) => (
              <FailureRowItem key={`${row.recipient_email}-${i}`} row={row} />
            ))}
          </div>
          {/* Aggregate disagrees with what we can show (cleanup cron pruned jobs). */}
          {!hasNextPage && rows.length < emailsFailed && (
            <p className="pt-3 text-xs text-muted-foreground">
              Showing {rows.length} of {emailsFailed}. Some failure details may have
              been cleaned up.
            </p>
          )}
          {hasNextPage && (
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Load more
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function CampaignDetail({ data }: { data: PartnerCampaignOverviewRow }): JSX.Element {
  return (
    <div className="divide-y">
      <DetailRow label="Workspace">{display(data.workspace_name)}</DetailRow>
      <DetailRow label="Campaign">{display(data.campaign_name)}</DetailRow>
      <DetailRow label="Status">
        <Badge variant="outline">{display(data.status)}</Badge>
      </DetailRow>
      <DetailRow label="Recipients">{display(data.recipient_count)}</DetailRow>
      <DetailRow label="Sent">{display(data.emails_sent)}</DetailRow>
      <DetailRow label="Failed">{display(data.emails_failed)}</DetailRow>
      <DetailRow label="Pending">{display(data.emails_pending)}</DetailRow>
      <DetailRow label="Failure rate">{formatRate(data.failure_rate)}</DetailRow>
      <DetailRow label="Unsubscribe rate">{formatRate(data.unsubscribe_rate)}</DetailRow>
      <DetailRow label="Real send error">
        <YesNo value={data.has_real_error} />
      </DetailRow>
      <DetailRow label="Attention">
        {data.needs_attention ? (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Needs attention
          </Badge>
        ) : (
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            OK
          </Badge>
        )}
      </DetailRow>
      <p className="pt-3 text-sm text-muted-foreground">
        To act on this campaign, log in as a member of {display(data.workspace_name)} from
        the Members tab.
      </p>
    </div>
  );
}

function SenderHealthBadge({
  status,
}: {
  status: PartnerSenderHealthRow['health_status'];
}): JSX.Element {
  switch (status) {
    case 'needs_reconnect':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertOctagon className="h-3 w-3" />
          Needs reconnect
        </Badge>
      );
    case 'expired':
      return (
        <Badge variant="default" className="gap-1 bg-yellow-500 hover:bg-yellow-500">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    case 'connected':
    default:
      return (
        <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
  }
}

function SenderDetail({ data }: { data: PartnerSenderHealthRow }): JSX.Element {
  return (
    <div className="divide-y">
      <DetailRow label="Sender">{display(data.email_address)}</DetailRow>
      <DetailRow label="Workspace">{display(data.workspace_name)}</DetailRow>
      <DetailRow label="Provider">
        <span className="capitalize">{display(data.provider)}</span>
      </DetailRow>
      <DetailRow label="Status">
        <SenderHealthBadge status={data.health_status} />
      </DetailRow>
      <DetailRow label="Emergency pause">
        <YesNo value={data.emergency_pause_active} />
      </DetailRow>
      <DetailRow label="Token refresh failures">
        {display(data.consecutive_token_refresh_failures)}
      </DetailRow>
      <DetailRow label="Token expires">{display(data.expires_at)}</DetailRow>
      <DetailRow label="Outlook 24h refresh risk">
        <YesNo value={data.outlook_spa_24h_risk} />
      </DetailRow>
      <p className="pt-3 text-sm text-muted-foreground">
        A member of {display(data.workspace_name)} must reconnect this account in that
        workspace&rsquo;s Settings &rarr; Senders.
      </p>
    </div>
  );
}

function QuotaDetail({ data }: { data: PartnerMemberQuotaRow }): JSX.Element {
  return (
    <div className="divide-y">
      <DetailRow label="Sender">{display(data.email_address)}</DetailRow>
      <DetailRow label="Workspace">{display(data.workspace_name)}</DetailRow>
      <DetailRow label="Sent today">{display(data.emails_sent_today)}</DetailRow>
      <DetailRow label="Daily limit">{display(data.daily_limit)}</DetailRow>
      <DetailRow label="Consumed">{formatRate(data.pct_consumed)}</DetailRow>
      <DetailRow label="Near limit">
        {data.near_wall ? (
          <Badge variant="default" className="gap-1 bg-yellow-500 hover:bg-yellow-500">
            <AlertTriangle className="h-3 w-3" />
            Near limit
          </Badge>
        ) : (
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            OK
          </Badge>
        )}
      </DetailRow>
    </div>
  );
}

function EmulationDetail({ data }: { data: PartnerEmulationActivityRow }): JSX.Element {
  return (
    <div className="divide-y">
      <DetailRow label="Admin">{display(data.admin_email ?? data.admin_user_id)}</DetailRow>
      <DetailRow label="Target">
        {display(data.target_email ?? data.target_user_id)}
      </DetailRow>
      <DetailRow label="Started">{display(data.started_at)}</DetailRow>
      <DetailRow label="Ended">{display(data.ended_at)}</DetailRow>
      <DetailRow label="End reason">
        {data.forced_by_security ? (
          <Badge variant="destructive" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            Forced by security
          </Badge>
        ) : (
          display(data.end_reason)
        )}
      </DetailRow>
      <DetailRow label="Read only">
        {data.read_only ? 'Yes' : <span className="text-muted-foreground">No</span>}
      </DetailRow>
      <DetailRow label="Reason">{display(data.reason)}</DetailRow>
    </div>
  );
}

/** Per-type header icon + title. */
function headerFor(record: EnterpriseDetailRecord): { icon: JSX.Element; title: string } {
  switch (record.type) {
    case 'campaign':
      return { icon: <Mail className="h-5 w-5" />, title: 'Campaign detail' };
    case 'sender':
      return { icon: <Send className="h-5 w-5" />, title: 'Sender detail' };
    case 'quota':
      return { icon: <Gauge className="h-5 w-5" />, title: 'Daily limit detail' };
    case 'emulation':
      return { icon: <ShieldAlert className="h-5 w-5" />, title: 'Emulation detail' };
  }
}

export function EnterpriseRecordDetailSheet({
  record,
  onOpenChange,
}: EnterpriseRecordDetailSheetProps): JSX.Element {
  const header = record ? headerFor(record) : null;

  return (
    <Sheet open={record !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-md"
        aria-describedby={undefined}
      >
        {record && header && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {header.icon}
                {header.title}
              </SheetTitle>
              <SheetDescription>
                Read-only detail from the latest monitoring data. Acting on it happens in
                the affected workspace.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              {record.type === 'campaign' && (
                <>
                  <CampaignDetail data={record.data} />
                  <CampaignFailuresSection
                    campaignId={record.data.campaign_id}
                    emailsFailed={record.data.emails_failed}
                  />
                </>
              )}
              {record.type === 'sender' && <SenderDetail data={record.data} />}
              {record.type === 'quota' && <QuotaDetail data={record.data} />}
              {record.type === 'emulation' && <EmulationDetail data={record.data} />}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default EnterpriseRecordDetailSheet;
